import * as Yup from 'yup';
import { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, Divider, InputAdornment, Link, MenuItem } from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  createCheque,
  updateCheque,
  TCreateChequeItem,
  createSuggestion,
  getChequeBankOptions,
  getChequeReceivedViaOptions,
} from 'src/redux/slices/cheque';

import { useSnackbar } from 'src/components/snackbar';
import FormProvider, {
  RHFSelect,
  RHFTextField,
  RHFCreatableAutocomplete,
} from 'src/components/hook-form';

import { ChequeCustomerType, ChequeStatus, IChequeItem } from 'src/types/cheque';

import { paths } from '../../routes/paths';

const DEFAULT_FIELD_HINTS = {
  CHEQUE_NUMBER: 'චෙක් අංකය',
  BANK: 'බැංකුව',
  CHEQUE_DATE: 'චෙක් පතෙහි සදහන් දිනය',
  AMOUNT: 'වටිනාකම',
  CUSTOMER: 'පාරිභෝගිකයාගේ නම',
  RECEIVED_VIA: 'ලැබුනේ (අලෙවි නියෝජිතයා)',
  RECEIVED_DATE: 'අප අතට ලැබුනු දිනය',
  CUSTOMER_TYPE: 'කස්ටමර් වර්ගය',
};

export type ConditionalSchema<T> = T extends string
  ? Yup.StringSchema
  : T extends number
  ? Yup.NumberSchema
  : T extends boolean
  ? Yup.BooleanSchema
  : T extends Record<any, any>
  ? Yup.AnyObjectSchema
  : T extends Array<any>
  ? Yup.ArraySchema<any, any>
  : Yup.AnySchema;

export type Shape<Fields> = {
  [Key in keyof Fields]: ConditionalSchema<Fields[Key]>;
};

type Props = {
  currentCheque?: IChequeItem;
};

export default function ChequeNewEditForm({ currentCheque }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useAppDispatcher();

  const bankOptions = useAppSelector((state) => state.cheque.bankOptions.data);
  const receivedViaOptions = useAppSelector((state) => state.cheque.receivedViaOptions.data);

  useEffect(() => {
    dispatch(getChequeBankOptions());
    dispatch(getChequeReceivedViaOptions());
  }, [dispatch]);

  const NewChequeCreateSchema = Yup.object<Shape<TCreateChequeItem>>().shape({
    customer: Yup.string().required('Customer is required'),
    accountNumber: Yup.number()
      .typeError('Account number must be a number')
      .required('Account number is required'),
    branchCode: Yup.string().required('Branch code is required'),
    payerAccountNumber: Yup.string().required('Payer account number is required'),
    payerName: Yup.string().nullable(),
    note: Yup.string().nullable().max(1000, 'Note must be at most 1000 characters'),
    receivedVia: Yup.string().required('Received person is required'),
    chequeNumber: Yup.string().required('Cheque number is required'),
    chequeDate: Yup.date().required('Cheque date is required'),
    receivedDate: Yup.date().required('Received date is required'),
    amountInCents: Yup.number().required('Amount is required'),
    bank: Yup.string().required('Bank is required'),
    invoice: Yup.string().nullable(),
    type: Yup.mixed<ChequeCustomerType>()
      .oneOf(Object.values(ChequeCustomerType))
      .required('Customer type is required'),
  });

  const defaultValues = useMemo(
    () => ({
      customer: currentCheque?.customer ?? '',
      accountNumber: currentCheque?.accountNumber ?? 0,
      branchCode: currentCheque?.branchCode ?? '',
      payerAccountNumber: currentCheque?.payerAccountNumber ?? '',
      payerName: currentCheque?.payerName ?? '',
      note: currentCheque?.note ?? '',
      receivedVia: currentCheque?.receivedVia ?? '',
      chequeNumber: currentCheque?.chequeNumber ?? '',
      chequeDate: currentCheque?.chequeDate ? new Date(currentCheque.chequeDate) : new Date(),
      amountInCents: currentCheque?.amountInCents ? Number(currentCheque.amountInCents / 100) : 0,
      bank: currentCheque?.bank ?? '',
      invoice: currentCheque?.invoice ?? null,
      receivedDate: currentCheque?.receivedDate ? new Date(currentCheque.receivedDate) : new Date(),
      type: currentCheque?.type ?? ChequeCustomerType.SALE,
    }),
    [currentCheque]
  );

  const methods = useForm({
    resolver: yupResolver(NewChequeCreateSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    if (currentCheque) {
      // update
      try {
        await dispatch(
          updateCheque({
            chequeID: currentCheque._id,
            updatingCheque: {
              ...data,
              amountInCents: data.amountInCents * 100,
              status: currentCheque.status,
              invoice: currentCheque.invoice,
            },
          })
        ).unwrap();

        enqueueSnackbar('Cheque updated successfully');
        navigate(paths.dashboard.cheque.root());
        reset();
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    } else {
      try {
        await dispatch(
          createCheque({
            newCheque: {
              ...data,
              amountInCents: data.amountInCents * 100,
              status: ChequeStatus.IN_HAND,
              invoice: data.invoice,
            },
          })
        ).unwrap();

        enqueueSnackbar('Cheque created successfully');
        navigate(paths.dashboard.cheque.root());
        reset();
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle2">Basic Information</Typography>
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField
                  required
                  name="chequeNumber"
                  label="Cheque Number"
                  helperText={DEFAULT_FIELD_HINTS.CHEQUE_NUMBER}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFCreatableAutocomplete
                  required
                  name="bank"
                  label="Bank"
                  helperText={DEFAULT_FIELD_HINTS.BANK}
                  options={bankOptions}
                  onCreateOption={(value) => dispatch(createSuggestion({ type: 'bank', value }))}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField required name="branchCode" label="Branch Code" />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField
                  required
                  name="payerAccountNumber"
                  label="Payer Account Number"
                  helperText="චෙක්පතේ සඳහන් ගිණුම් අංකය"
                />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField
                  name="payerName"
                  label="Payer Name"
                  transform={(value) => value.toUpperCase()}
                  helperText="චෙක්පතේ සඳහන් ගිණුම් හිමියාගේ නම (සඳහන් නොවුනහොත් හිස්ව තබන්න)"
                />
              </Grid>

              <Grid xs={12} md={6}>
                <Controller
                  name="chequeDate"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      label="Cheque Date"
                      value={field.value}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message || DEFAULT_FIELD_HINTS.CHEQUE_DATE,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField
                  required
                  name="amountInCents"
                  label="Cheque Amount"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Rs</InputAdornment>,
                  }}
                  type="number"
                  helperText={DEFAULT_FIELD_HINTS.AMOUNT}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField
                  required
                  name="customer"
                  label="Customer Name"
                  helperText={DEFAULT_FIELD_HINTS.CUSTOMER}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <RHFTextField
                  required
                  name="accountNumber"
                  label="Salesbinder Account Number"
                  type="number"
                  helperText="Salesbinder සිස්ටම් එකේ කස්ටමර් නම්බර් එක"
                />
              </Grid>

              <Grid xs={12} md={4}>
                <RHFSelect fullWidth name="type" label="Customer Type" required>
                  {Object.values(ChequeCustomerType).map((type) => (
                    <MenuItem
                      key={type}
                      value={type}
                      sx={{
                        textTransform: 'capitalize',
                      }}
                    >
                      {type.split('_').join(' ')}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Grid>

              <Grid xs={12} md={6}>
                <RHFCreatableAutocomplete
                  required
                  name="receivedVia"
                  label="Received Via"
                  helperText={DEFAULT_FIELD_HINTS.RECEIVED_VIA}
                  options={receivedViaOptions}
                  onCreateOption={(value) =>
                    dispatch(createSuggestion({ type: 'receivedVia', value }))
                  }
                />
              </Grid>

              <Grid xs={12} md={6}>
                <Controller
                  name="receivedDate"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DatePicker
                      label="Received Date"
                      value={field.value}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message || DEFAULT_FIELD_HINTS.RECEIVED_DATE,
                          required: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12}>
                <RHFTextField
                  name="invoice"
                  label="Invoice URL"
                  placeholder="https://uveeagle.salesbinder.com/v2/documents/view/xxxxx-xxxxx-xxxx-xxxx-xxxx"
                />
              </Grid>

              <Grid xs={12}>
                <RHFTextField
                  name="note"
                  label="Note"
                  multiline
                  rows={3}
                  placeholder="Any extra information about this cheque"
                />
              </Grid>

              <Divider
                sx={{
                  width: 1,
                  my: 2.5,
                  borderStyle: 'dashed',
                }}
              />
            </Grid>

            <Stack direction="row" justifyContent="flex-end" gap={2} mt={4}>
              <Link component={RouterLink} href={paths.dashboard.cheque.root()}>
                <Button variant="outlined" color="inherit">
                  Cancel
                </Button>
              </Link>

              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                {!currentCheque ? 'Create Cheque' : 'Save Changes'}
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
